<script>
import { setContext } from "svelte";
import { clickoutside } from "@svelte-put/clickoutside";
import { MENU_BAR_KEY } from "./menuContext.js";
import {
    focusItem,
    getEnabledItems,
    getMenuPanel,
    isMenuNavigationKey,
} from "./menuKeyboard.js";

/** @type {{ children: import('svelte').Snippet }} */
let { children } = $props();

const state = $state({
    openId: /** @type {string | null} */ (null),
});

/** @type {{ id: string, trigger: HTMLElement }[]} */
let menus = [];

function register(id, trigger) {
    const existing = menus.find((menu) => menu.id === id);
    if (existing) {
        existing.trigger = trigger;
        return;
    }
    menus.push({ id, trigger });
}

function unregister(id) {
    const index = menus.findIndex((menu) => menu.id === id);
    if (index !== -1) {
        menus.splice(index, 1);
    }
}

function open(id) {
    state.openId = id;
}

function closeAll() {
    state.openId = null;
}

function isOpen(id) {
    return state.openId === id;
}

function isAnyOpen() {
    return state.openId !== null;
}

function openAdjacent(direction) {
    const ids = menus.map((menu) => menu.id);
    const index = ids.indexOf(state.openId);
    if (index < 0 || ids.length === 0) {
        return;
    }
    const next = ids[(index + direction + ids.length) % ids.length];
    state.openId = next;
    return menus.find((menu) => menu.id === next);
}

function openSubmenu(trigger) {
    trigger.dispatchEvent(new CustomEvent("cc-menu-open"));
    requestAnimationFrame(() => {
        const panel = trigger
            .closest("[data-submenu]")
            ?.querySelector(":scope > [role='menu']");
        focusItem(getEnabledItems(panel)[0]);
    });
}

function focusPanelFirst(menu) {
    const panel = getMenuPanel(menu?.trigger);
    focusItem(getEnabledItems(panel)[0] ?? menu?.trigger);
}

function handleKeydown(event) {
    if (!state.openId || !isMenuNavigationKey(event.key)) {
        return;
    }

    event.stopPropagation();
    event.stopImmediatePropagation();

    if (event.key === "Tab") {
        closeAll();
        return;
    }

    event.preventDefault();

    const active = document.activeElement;
    const panel = active?.closest?.("[role='menu']");
    const enabled = getEnabledItems(panel);
    const currentIndex = enabled.indexOf(active);
    const isSubTrigger =
        active?.getAttribute("aria-haspopup") === "true" && !!panel;

    if (event.key === "Escape") {
        if (panel?.dataset.submenu === "true") {
            const trigger = panel.parentElement?.querySelector(
                "[data-submenu-trigger]",
            );
            trigger?.dispatchEvent(new CustomEvent("cc-menu-close"));
            trigger?.focus();
            return;
        }
        const menu = menus.find((item) => item.id === state.openId);
        closeAll();
        menu?.trigger?.focus();
        return;
    }

    if (event.key === "ArrowDown") {
        if (!panel) {
            focusPanelFirst(
                menus.find((item) => item.id === state.openId),
            );
            return;
        }
        if (enabled.length) {
            focusItem(enabled[(currentIndex + 1) % enabled.length]);
        }
        return;
    }

    if (event.key === "ArrowUp") {
        if (!panel) {
            const menu = menus.find((item) => item.id === state.openId);
            const items = getEnabledItems(getMenuPanel(menu?.trigger));
            focusItem(items[items.length - 1] ?? menu?.trigger);
            return;
        }
        if (enabled.length) {
            focusItem(
                enabled[(currentIndex - 1 + enabled.length) % enabled.length],
            );
        }
        return;
    }

    if (event.key === "Home" && enabled.length) {
        focusItem(enabled[0]);
        return;
    }

    if (event.key === "End" && enabled.length) {
        focusItem(enabled[enabled.length - 1]);
        return;
    }

    if (event.key === "ArrowRight") {
        if (isSubTrigger) {
            openSubmenu(active);
            return;
        }
        const nextMenu = openAdjacent(1);
        requestAnimationFrame(() => focusPanelFirst(nextMenu));
        return;
    }

    if (event.key === "ArrowLeft") {
        if (panel?.dataset.submenu === "true") {
            const trigger = panel.parentElement?.querySelector(
                "[data-submenu-trigger]",
            );
            trigger?.dispatchEvent(new CustomEvent("cc-menu-close"));
            trigger?.focus();
            return;
        }
        const nextMenu = openAdjacent(-1);
        requestAnimationFrame(() => focusPanelFirst(nextMenu));
        return;
    }

    if (event.key === "Enter" || event.key === " ") {
        if (isSubTrigger) {
            openSubmenu(active);
            return;
        }
        if (active?.getAttribute("aria-disabled") !== "true") {
            active?.click();
        }
    }
}

setContext(MENU_BAR_KEY, {
    get openId() {
        return state.openId;
    },
    register,
    unregister,
    open,
    closeAll,
    isOpen,
    isAnyOpen,
});
</script>

<svelte:window onkeydowncapture={handleKeydown} />

<div
    class="menu-bar-root"
    use:clickoutside
    onclickoutside={closeAll}>
    {@render children()}
</div>

<style>
.menu-bar-root {
    display: flex;
    flex-grow: 1;
    width: 100%;
    align-items: center;
}
</style>
